import { useCallback, useEffect, useRef, useState } from "react";
import { useVideoPlayer2 } from "../hooks/use-video-player-2-setup-start";
import { useVideoPlayer2ForwardRewind } from "../hooks/useVideoPlayer2Play";

export function DoubleTapSeek() {
  const player = useVideoPlayer2();
  const { forward, rewind } = useVideoPlayer2ForwardRewind();
  const [showIndicator, setShowIndicator] = useState(false);
  const [indicatorType, setIndicatorType] = useState<'forward' | 'backward' | null>(null);
  const [indicatorPosition, setIndicatorPosition] = useState({ x: 0, y: 0 });
  
  const containerRef = useRef<HTMLDivElement>(null);
  const lastTapTimeRef = useRef<number>(0);
  const tapCountRef = useRef<number>(0);
  const tapTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const indicatorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Double tap detection
  const handleTap = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;
    
    const now = Date.now();
    const timeSinceLastTap = now - lastTapTimeRef.current;
    const rect = containerRef.current.getBoundingClientRect();
    const { clientX, clientY } = e;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    // Clear any existing timeout
    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
    }
    
    // Check if this is a double tap (within 300ms and similar position)
    if (timeSinceLastTap < 300 && tapCountRef.current === 1) {
      // Double tap detected!
      const isLeftSide = x < rect.width / 2;
      const seekType = isLeftSide ? 'backward' : 'forward';
      
      // Perform the seek action
      if (seekType === 'forward') {
        forward(10);
      } else {
        rewind(10);
      }
      
      // Show visual indicator
      setIndicatorType(seekType);
      setIndicatorPosition({ x: clientX, y: clientY });
      setShowIndicator(true);
      
      // Hide indicator after animation
      if (indicatorTimeoutRef.current) {
        clearTimeout(indicatorTimeoutRef.current);
      }
      indicatorTimeoutRef.current = setTimeout(() => {
        setShowIndicator(false);
        setIndicatorType(null);
      }, 1000);
      
      // Reset tap count
      tapCountRef.current = 0;
      lastTapTimeRef.current = 0;
    } else {
      // First tap or too much time passed
      tapCountRef.current = 1;
      lastTapTimeRef.current = now;
      
      // Set timeout to reset tap count if no second tap comes
      tapTimeoutRef.current = setTimeout(() => {
        tapCountRef.current = 0;
        lastTapTimeRef.current = 0;
      }, 300);
    }
  }, [forward, rewind]);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
      if (indicatorTimeoutRef.current) {
        clearTimeout(indicatorTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="video-player-2-double-tap-seek"
      // Touch taps are handled through the synthesized click: on touchend
      // `touches` is empty (reading `touches[0].clientX` threw on every tap),
      // and handling both events would count one tap as a double tap.
      onClick={handleTap}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
        pointerEvents: 'auto',
        cursor: 'pointer'
      }}
    >
      {/* Visual indicator for double tap */}
      {showIndicator && indicatorType && (
        <div
          className={`video-player-2-double-tap-seek__indicator video-player-2-double-tap-seek__indicator--${indicatorType}`}
          style={{
            position: 'fixed',
            left: indicatorPosition.x,
            top: indicatorPosition.y,
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            pointerEvents: 'none'
          }}
        >
          <div className="video-player-2-double-tap-seek__icon">
            {indicatorType === 'forward' ? '⏩' : '⏪'}
          </div>
          <div className="video-player-2-double-tap-seek__text">
            {indicatorType === 'forward' ? '+10s' : '-10s'}
          </div>
        </div>
      )}
    </div>
  );
}
